import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  forwardRef,
  inject,
  Input,
  OnInit,
} from '@angular/core';
import {
  ControlContainer,
  ControlValueAccessor,
  FormControl,
  FormGroup,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
} from '@angular/forms';
import { InputComponent } from '@components/controls/input/input.component';
import { TripType } from '@constants/home.constants';
import { LetDirective } from '@directives/app-let.directive';
import { SearchWidgetCopy } from '@models/cms/search-widget.model';
import { FeatureFlagsEnum } from '@models/environment.type';
import { FeatureFlagService } from '@services/feature-flag/feature-flag.service';
import { Store } from '@ngrx/store';
import { selectAppCmsCopy } from '@selectors/cms.selector';
import { selectShouldUseMobileStyling } from '@selectors/app.selector';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {GrowthBookFeatureKeys} from "@models/growthbook.models";
import {GrowthBookService} from "@services/growthbook/growthbook.service";
import {SvgIconComponent} from "@components/svg-icon/svg-icon.component";
import {
  RoundTripDiscountAlertComponent
} from "@components/round-trip-discount-alert/round-trip-discount-alert.component";
import { selectIsModifyPath, selectIsFlightsPage, selectIsHomePage, selectIsUpgradePath } from '@selectors/router.selector';

@Component({
  selector: 'app-trip-type-picker',
  standalone: true,
  imports: [
    CommonModule,
    LetDirective,
    ReactiveFormsModule,
    InputComponent,
    SvgIconComponent,
    RoundTripDiscountAlertComponent,
  ],
  templateUrl: './trip-type-picker.component.html',
  styleUrls: ['./trip-type-picker.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => TripTypePickerComponent),
      multi: true,
    },
  ],
})
export class TripTypePickerComponent implements OnInit, ControlValueAccessor {
  private destroyRef = inject(DestroyRef);
  value: string;
  changed: (value: string) => string;
  touched: () => void;
  tripTypeFormGroup!: FormGroup;
  displayPromoCode = false;

  @Input()
  cmsSearchWidgetCopy: SearchWidgetCopy;
  @Input() isPromoCode: boolean = false;

  get tripTypeControl(): FormControl {
    return this.tripTypeFormGroup?.get('tripType') as FormControl;
  }

  cmsAppCopy$ = this.store.select(selectAppCmsCopy);
  shouldUseMobileStyling$ = this.store.select(selectShouldUseMobileStyling);
  isModifyPath$ = this.store.select(selectIsModifyPath);
  isFlightsPage$ = this.store.select(selectIsFlightsPage);
  isHomePage$ = this.store.select(selectIsHomePage);
  isUpgradePath$ = this.store.select(selectIsUpgradePath);
  
  readonly tripType = TripType;
  readonly FeatureFlagsEnum = FeatureFlagsEnum;

  constructor(
    private controlContainer: ControlContainer,
    private store: Store,
    public featureFlagService: FeatureFlagService,
    private cd: ChangeDetectorRef,
    public gbs: GrowthBookService,
  ) {}

  ngOnInit(): void {
    this.tripTypeFormGroup = this.controlContainer.control as FormGroup;

    this.displayPromoCode =
      this.featureFlagService.isFeatureActive(
        FeatureFlagsEnum.PROMO_INPUT_OPEN,
      ) || !!this.tripTypeFormGroup?.controls?.promoCode?.value;

    this.tripTypeFormGroup.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.cd.detectChanges();
      });
  }

  // ControlValueAccessor interface methods implementation
  writeValue(value: TripType): void {
    this.value = value;
  }

  registerOnChange(fn: () => string): void {
    this.changed = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.touched = fn;
  }

  toggleValue() {
    this.tripTypeControl.setValue(this.tripTypeControl.value === this.tripType.oneWay ? this.tripType.roundTrip : this.tripType.oneWay)
  }

  protected readonly gbf = GrowthBookFeatureKeys;
}
